import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  DataOnlyDocument,
  ListCommentsWithRepliesGQL,
} from '../../../generated/graphql.generated';
import { PlusApollo } from '../../core/auth/graphql.module';
import { GraphQLModule } from '../../core/public-api';
import { currentTimeSeconds } from '../../time';
import { DashboardStore } from '../dashboardv2.store';

interface ActivationRow {
  jobCode: string;
  jobUrl: string;
  customerName: string;
  customerUrl: string;
  totalCost: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  mode: string;
  threadId: string;
  createdAt: number;
  comments?: any[];
}

@Component({
  selector: 'app-dashboard-ai-activations',
  templateUrl: './dashboard-ai-activations.component.html',
  styleUrls: [
    '../dashboard-billing/dashboard-billing.component.scss',
    './dashboard-ai-activations.component.scss',
  ],
})
export class DashboardAiActivationsComponent implements OnInit, OnDestroy {

  tableData: ActivationRow[] = [];
  loading = true;
  lastReload: number | null = null;
  first = 0;
  rowsPerPage = 25;

  private subs: Subscription[] = [];

  constructor(
    private dashboardStore: DashboardStore,
    private graphqlModule: GraphQLModule,
    private apollo: PlusApollo,
    private listCommentsGQL: ListCommentsWithRepliesGQL,
  ) {}

  expandedRowKeys: Record<string, boolean> = {};

  triggerReload(): void {
    this.dashboardStore.triggerReload();
  }

  ngOnInit(): void {
    this.subs.push(
      combineLatest([
        this.dashboardStore.unixPeriod$,
        this.dashboardStore.reloadKey$,
      ]).subscribe(() => {
        this.loadData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rowsPerPage = event.rows;
  }

  async toggleExpand(row: ActivationRow): Promise<void> {
    if (this.expandedRowKeys[row.threadId]) {
      this.expandedRowKeys = { ...this.expandedRowKeys, [row.threadId]: undefined };
    } else {
      this.expandedRowKeys = { ...this.expandedRowKeys, [row.threadId]: true };
      if (!row.comments) {
        this.loading = true;
        try {
          const result = await this.listCommentsGQL.fetch(
            { filter: { threadIds: [row.threadId] } },
            { fetchPolicy: 'network-only' },
          ).toPromise();
          const comments = [...((result as any)?.data?.comments?.comments || [])];
          row.comments = comments.sort((a: any, b: any) => (a.createdAt || 0) - (b.createdAt || 0));
        } catch (e) {
          console.error('Error fetching conversation', e);
        } finally {
          this.loading = false;
        }
      }
    }
  }

  private loadData(): void {
    const zone = this.graphqlModule.zone.value;
    if (!zone) { return; }

    this.loading = true;
    this.expandedRowKeys = {};

    this.dashboardStore.unixPeriod$.pipe(take(1)).subscribe(({ start, end }) => {
      this.subs.push(
        this.apollo.query({
          query: DataOnlyDocument,
          variables: {
            start,
            end,
            periodType: 'activationCreatedAt',
            groupBy: 'ai_conversation',
            dataColumns: [],
            performance: 'skip-count',
            skip: 0,
            limit: -1,
            zone,
          },
        }).subscribe(({ data }: any) => {
          const rows = data?.previewReport?.data?.rows ?? [];
          const headers = data?.previewReport?.data?.headers ?? [];
          this.tableData = this.parseRows(headers, rows);
          this.loading = false;
          this.lastReload = currentTimeSeconds();
          this.dashboardStore.setLastReload(this.lastReload);
        }),
      );
    });
  }

  private parseRows(headers: string[], rows: string[][]): ActivationRow[] {
    return rows.map((row) => {
      const getNum = (col: string) => {
        const val = row[headers.indexOf(col)];
        return val ? parseFloat(val.replace(/[$,]/g, '')) : 0;
      };

      const getStr = (col: string) => {
        return row[headers.indexOf(col)] || '';
      };

      const parseLink = (colName: string): { label: string; url: string } => {
        const raw = row[headers.indexOf(colName)];
        if (!raw || raw === 'None') return { label: 'N/A', url: '' };
        const htmlMatch = raw.match(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/);
        if (htmlMatch) {
          return { label: htmlMatch[2], url: htmlMatch[1] };
        }
        const angleMatch = raw.match(/^(.+?)\s*<([^>]+)>$/);
        if (angleMatch) {
          return { label: angleMatch[1].trim(), url: angleMatch[2].trim() };
        }
        try {
          const obj = JSON.parse(raw);
          return { label: obj.code || `${obj.givenName || ''} ${obj.familyName || ''}`.trim() || 'Unknown', url: '' };
        } catch {
          return { label: raw, url: '' };
        }
      };

      const job = parseLink('Job');
      const customer = parseLink('Customer');

      return {
        jobCode: job.label,
        jobUrl: job.url,
        customerName: customer.label,
        customerUrl: customer.url,
        totalCost: getNum('Total Cost'),
        totalTokens: getNum('Total Tokens'),
        promptTokens: getNum('Prompt Tokens'),
        completionTokens: getNum('Completion Tokens'),
        mode: getStr('Mode') || 'Unknown',
        threadId: getStr('Thread ID') || '',
        createdAt: getNum('Created At'),
      };
    });
  }
}